import * as d3 from "d3";
import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import DiagramRelationship from "@semantical/modules/diagram/DiagramRelationship.ts";

/**
 * Configuration options for the force layout engine.
 */
export interface ForceLayoutConfig {
    /** Distance between linked nodes (default: 250) */
    linkDistance?: number;
    /** Strength of link force (default: 0.3) */
    linkStrength?: number;
    /** Repulsion strength between nodes (default: -2000) */
    chargeStrength?: number;
    /** Maximum distance for charge force (default: 600) */
    chargeDistanceMax?: number;
    /** Extra padding around collision radius (default: 40) */
    collisionPadding?: number;
    /** Strength of collision force (default: 0.8) */
    collisionStrength?: number;
    /** Strength of centering force (default: 0.05) */
    centerStrength?: number;
    /** Alpha decay rate - lower = longer settling time (default: 0.02) */
    alphaDecay?: number;
}

/**
 * Callback type for position updates during simulation.
 */
export type PositionUpdateCallback = (diagramClass: DiagramClass, x: number, y: number) => void;

/**
 * Callback type for simulation tick events.
 */
export type TickCallback = () => void;

/**
 * Internal node type used by the simulation.
 * Extends DiagramClass with D3 simulation properties.
 */
interface SimulationNode extends d3.SimulationNodeDatum {
    diagramClass: DiagramClass;
    id: string;
    width: number;
    height: number;
}

/**
 * Internal link type used by the simulation.
 */
interface SimulationLink {
    source: SimulationNode;
    target: SimulationNode;
    relationship: DiagramRelationship;
}

/**
 * ForceLayoutEngine encapsulates D3 force simulation logic for automatic
 * arrangement of diagram classes. It provides a clean interface for the
 * DiagramEditor without exposing simulation implementation details.
 */
export class ForceLayoutEngine {
    private simulation: d3.Simulation<SimulationNode, SimulationLink> | null = null;
    private nodes: SimulationNode[] = [];
    private links: SimulationLink[] = [];
    private config: Required<ForceLayoutConfig>;
    private onPositionUpdate: PositionUpdateCallback;
    private onTick: TickCallback;
    private center: { x: number; y: number } = { x: 400, y: 300 };

    private static readonly DEFAULT_CONFIG: Required<ForceLayoutConfig> = {
        linkDistance: 250,
        linkStrength: 0.3,
        chargeStrength: -2000,
        chargeDistanceMax: 600,
        collisionPadding: 40,
        collisionStrength: 0.8,
        centerStrength: 0.05,
        alphaDecay: 0.02
    };

    constructor(
        onPositionUpdate: PositionUpdateCallback,
        onTick: TickCallback,
        config?: ForceLayoutConfig
    ) {
        this.onPositionUpdate = onPositionUpdate;
        this.onTick = onTick;
        this.config = { ...ForceLayoutEngine.DEFAULT_CONFIG, ...config };
    }

    /**
     * Check if the simulation is currently running.
     */
    isRunning(): boolean {
        return this.simulation !== null;
    }

    /**
     * Set the center point for the centering force.
     */
    setCenter(x: number, y: number): void {
        this.center = { x, y };
        if (this.simulation) {
            const centerForce = this.simulation.force("center") as d3.ForceCenter<SimulationNode>;
            if (centerForce) {
                centerForce.x(x).y(y);
            }
        }
    }

    /**
     * Start the force simulation with the given diagram classes and relationships.
     * @param classes - The diagram classes to simulate
     * @param relationships - The relationships between classes
     * @param initialAlpha - Initial energy level (0-1). Use 1 for new layouts, low values (e.g., 0.01) to preserve existing positions.
     */
    start(classes: DiagramClass[], relationships: DiagramRelationship[], initialAlpha: number = 1): void {
        this.stop();

        // Create simulation nodes from diagram classes
        this.nodes = classes.map(dc => ({
            diagramClass: dc,
            id: dc.modelElement.uri,
            width: dc.width,
            height: dc.height,
            // Initialize position at center of class (simulation uses center coordinates)
            x: dc.x + dc.width / 2,
            y: dc.y + dc.height / 2
        }));

        // Create node lookup map for links
        const nodeById = new Map(this.nodes.map(n => [n.id, n]));

        // Create simulation links from relationships
        this.links = relationships
            .map(rel => {
                const source = nodeById.get(rel.source.modelElement.uri);
                const target = nodeById.get(rel.target.modelElement.uri);
                if (source && target) {
                    return {
                        source,
                        target,
                        relationship: rel
                    };
                }
                return null;
            })
            .filter((link): link is SimulationLink => link !== null);

        // Create and configure the simulation
        this.simulation = d3.forceSimulation<SimulationNode>(this.nodes)
            .force("link", d3.forceLink<SimulationNode, d3.SimulationLinkDatum<SimulationNode>>(this.links as d3.SimulationLinkDatum<SimulationNode>[])
                .id(d => d.id)
                .distance(this.config.linkDistance)
                .strength(this.config.linkStrength))
            .force("charge", d3.forceManyBody<SimulationNode>()
                .strength(this.config.chargeStrength)
                .distanceMax(this.config.chargeDistanceMax))
            .force("center", d3.forceCenter<SimulationNode>(this.center.x, this.center.y)
                .strength(this.config.centerStrength))
            .force("collide", d3.forceCollide<SimulationNode>()
                // Use half-diagonal of the rectangle to ensure full coverage for rectangular nodes
                // plus padding margin to create spacing between classes
                .radius(d => {
                    const w = d.diagramClass.width;
                    const h = d.diagramClass.height;
                    // Half-diagonal ensures the circle fully encloses the rectangle
                    const halfDiagonal = Math.sqrt(w * w + h * h) / 2;
                    return halfDiagonal + this.config.collisionPadding;
                })
                .strength(this.config.collisionStrength))
            .alphaDecay(this.config.alphaDecay)
            .on("tick", () => this.handleTick());

        // Start with specified energy level
        this.simulation.alpha(initialAlpha).restart();
    }

    /**
     * Stop the simulation and clean up.
     */
    stop(): void {
        if (this.simulation) {
            this.simulation.stop();
            this.simulation = null;
        }
        this.nodes = [];
        this.links = [];
    }

    /**
     * Restart the simulation with a given alpha value.
     * Useful when the diagram structure changes while simulation is active.
     */
    restart(alpha: number = 0.5): void {
        if (this.simulation) {
            this.simulation.alpha(alpha).restart();
        }
    }

    /**
     * Pin a node at a specific position (used during drag).
     * The node will stay fixed while other nodes continue to be simulated.
     */
    pinNode(diagramClass: DiagramClass, x: number, y: number): void {
        const node = this.nodes.find(n => n.diagramClass === diagramClass);
        if (node) {
            // Convert from top-left to center coordinates
            node.fx = x + diagramClass.width / 2;
            node.fy = y + diagramClass.height / 2;
        }
    }

    /**
     * Release a pinned node, allowing it to be affected by forces again.
     */
    releaseNode(diagramClass: DiagramClass): void {
        const node = this.nodes.find(n => n.diagramClass === diagramClass);
        if (node) {
            node.fx = null;
            node.fy = null;
        }
    }

    /**
     * Update a pinned node's position and reheat the simulation slightly.
     */
    updatePinnedNode(diagramClass: DiagramClass, x: number, y: number): void {
        this.pinNode(diagramClass, x, y);
        // Reheat simulation so other nodes adjust
        this.simulation?.alpha(0.3).restart();
    }

    /**
     * Handle simulation tick - update all node positions.
     */
    private handleTick(): void {
        for (const node of this.nodes) {
            if (node.x !== undefined && node.y !== undefined) {
                // Convert from center to top-left coordinates using actual DiagramClass dimensions
                const newX = node.x - node.diagramClass.width / 2;
                const newY = node.y - node.diagramClass.height / 2;
                this.onPositionUpdate(node.diagramClass, newX, newY);
            }
        }
        this.onTick();
    }

    /**
     * Notify the engine that node dimensions have changed.
     * Reinitializes the collision force and reheats the simulation.
     */
    notifyDimensionsChanged(): void {
        if (this.simulation) {
            // Reinitialize the collision force with updated radius calculation
            this.simulation.force("collide", d3.forceCollide<SimulationNode>()
                .radius(d => {
                    const w = d.diagramClass.width;
                    const h = d.diagramClass.height;
                    const halfDiagonal = Math.sqrt(w * w + h * h) / 2;
                    return halfDiagonal + this.config.collisionPadding;
                })
                .strength(this.config.collisionStrength));

            // Reheat simulation to allow collision detection to readjust
            this.simulation.alpha(0.5).restart();
        }
    }

    /**
     * Update configuration and apply to running simulation.
     */
    updateConfig(config: Partial<ForceLayoutConfig>): void {
        this.config = { ...this.config, ...config };

        if (this.simulation) {
            const linkForce = this.simulation.force("link") as d3.ForceLink<SimulationNode, SimulationLink>;
            if (linkForce && config.linkDistance !== undefined) {
                linkForce.distance(config.linkDistance);
            }
            if (linkForce && config.linkStrength !== undefined) {
                linkForce.strength(config.linkStrength);
            }

            const chargeForce = this.simulation.force("charge") as d3.ForceManyBody<SimulationNode>;
            if (chargeForce && config.chargeStrength !== undefined) {
                chargeForce.strength(config.chargeStrength);
            }

            const collideForce = this.simulation.force("collide") as d3.ForceCollide<SimulationNode>;
            if (collideForce && config.collisionStrength !== undefined) {
                collideForce.strength(config.collisionStrength);
            }

            // Reheat to apply new forces
            this.simulation.alpha(0.3).restart();
        }
    }
}
